import React from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import { useDispatch } from 'store/hooks';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { useQueryClient } from '@tanstack/react-query';
import { BudgetExpenseOrIncome } from 'business/constants';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { useAddInscription } from 'routes/BudgetModule/components/BudgetProposal/Resource/ExpenseIncome/hooks/inscriptions';
import WithLoader from 'ui/WithLoader/WithLoader';
import { UpdateDisabledTooltip } from 'routes/BudgetModule/components/shared/InscriptionsGrid/UpdateDisabledTooltip';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { BudgetProposalEdition } from '../../hooks/budgetProposalEdition';

type Props = {
    budgetProposal: BudgetProposalT;
    expenseOrIncome: BudgetExpenseOrIncome;
    budgetProposalEdition: BudgetProposalEdition;
    resourceSoftwareId: string;
    resourceName: string;
    inscriptionsQueryCacheKey: InscriptionQueryKey;
};

export const AddOrphanInscriptions = ({
    budgetProposal,
    expenseOrIncome,
    budgetProposalEdition,
    resourceSoftwareId,
    resourceName,
    inscriptionsQueryCacheKey,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const addInscriptionMutation = useAddInscription(
        (error) => {
            dispatch.vision.notifyUser({
                message: error.message,
                snackType: 'error',
            });
        },
        (message) => {
            dispatch.vision.notifyUser({
                message,
                snackType: 'success',
            });
            trackEventBudget(
                TRACKABLE_EVENTS.budget.budgetProposal.expenseIncome.imputations
                    .addOrphanInscription,
                {
                    budgetProposalName: budgetProposal.name,
                    phase: budgetProposal.phase,
                    resourceName,
                },
            );
        },
        queryClient,
        budgetProposalEdition.canAddInscription,
        budgetProposal.phase,
        {
            budgetProposalId: budgetProposal.id,
            expenseIncome: expenseOrIncome,
            resourceSoftwareId,
        },
        inscriptionsQueryCacheKey,
    );

    const {
        canAddInscription,
        canAddInscriptionInPhase,
        isEditionOpenToResource,
    } = budgetProposalEdition;

    return (
        <Box sx={{ paddingBottom: '1em' }}>
            <Grid container alignItems="center" className="subGrid">
                {!addInscriptionMutation.isLoading ? (
                    <UpdateDisabledTooltip
                        disabledByResource={
                            canAddInscriptionInPhase &&
                            !canAddInscription &&
                            isEditionOpenToResource
                        }
                        isSupplementaryNotEditable={isEditionOpenToResource}
                        disabledByRights={!isEditionOpenToResource}
                        disabledByPhase={!canAddInscriptionInPhase}
                        hidden={false}
                        by={budgetProposal.by}
                    >
                        <Box>
                            <Button
                                variant="contained"
                                size="small"
                                sx={{
                                    minHeight: '1.75rem',
                                    marginLeft: '1.5rem',
                                }}
                                onClick={addInscriptionMutation.mutate}
                                startIcon={<AddIcon fontSize="small" />}
                                disabled={!canAddInscription}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '11.5px',
                                        color: 'inherit',
                                    }}
                                    variant="body1SemiBold"
                                >
                                    {t('budgetProposal.addOrphanInscription')}
                                </Typography>
                            </Button>
                        </Box>
                    </UpdateDisabledTooltip>
                ) : (
                    <WithLoader isLoading>
                        <Button sx={{ opacity: 0 }} disabled>
                            {t('budgetProposal.addOrphanInscription')}
                        </Button>
                    </WithLoader>
                )}
            </Grid>
        </Box>
    );
};
